import { Fragment, memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import DropdownMenu from './DropdownMenu';
import MenuItem from './MenuItem';
import ModelIcon from './ModelIcon';

/**
 * Select - Custom dropdown component with consistent styling
 * Replaces native <select> elements for better control and theming
 * 
 * @param {string} label - Label text for the dropdown
 * @param {any} value - Currently selected value
 * @param {function} onChange - Callback when value changes (receives new value)
 * @param {Array} options - Array of {value, label, modelName?, icon?, iconKey?, disabled?, group?} objects.
 *   `group` renders a static, non-interactive heading above the first option of each run of
 *   options sharing it. Grouped options must be contiguous; ungrouped lists render unchanged.
 *   - icon: Optional leading icon shown in the trigger and menu row
 *   - iconKey: Optional key to show model icon on the right (uses modelIconMapper)
 *   - disabled: Optional; renders the row as a non-selectable, dimmed entry
 * @param {boolean} disabled - Whether dropdown is disabled
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {'below'|'above'} position - Menu opens below (default) or above the trigger
 * @param {boolean} multiSelect - If true, menu stays open after item click
 * @param {boolean} showSecondaryInTrigger - Whether to show modelName in the closed trigger
 * @param {string} ariaLabel - Accessible label for unlabeled selects
 * @param {'default'|'silent'} appearance - Trigger chrome treatment
 * @param {'default'|'media'} iconVariant - Leading icon layout for the trigger and menu rows
 * @param {'default'|'checkmark'} selectionVariant - Selected row treatment
 * @param {function} onOptionPreview - Optional. Called with an option value when that row becomes
 *   hot (mouseenter or focus), and with null when the pointer or keyboard focus leaves the menu
 *   surface while it stays open ("no candidate — show the base value"). Enables hover auditioning;
 *   the caller owns the previewed value and its restore. Ignored when multiSelect is true.
 * @param {function} onOptionPreviewEnd - Optional. Called once when a preview session ends WITHOUT
 *   a selection: the menu closed (outside click, Escape, trigger toggle) or Select unmounted while
 *   a row was hot. Selecting a row ends the session silently instead, because onChange supersedes.
 */
const Select = ({
  label,
  ariaLabel,
  value,
  onChange,
  options = [],
  disabled = false,
  placeholder = 'Select...',
  position = 'below',
  multiSelect = false,
  showSecondaryInTrigger = true,
  appearance = 'default',
  iconVariant = 'default',
  selectionVariant = 'default',
  portalRoot = null,
  renderMenuFooter = null,
  focusOpenIntent = null,
  onOptionPreview = null,
  onOptionPreviewEnd = null,
}) => {
  const [manuallyOpen, setManuallyOpen] = useState(false);
  const [dismissedFocusOpenIntent, setDismissedFocusOpenIntent] = useState(null);
  const triggerRef = useRef(null);
  const handledFocusOpenIntentRef = useRef(null);
  // Whether a row has been hot since the menu opened. Ref, not state: it gates callbacks
  // only and must never schedule a render mid-hover.
  const previewSessionRef = useRef(false);
  const previewEndRef = useRef(onOptionPreviewEnd);
  const previewEnabled = typeof onOptionPreview === 'function' && !multiSelect;
  const isIntentOpen = !disabled
    && focusOpenIntent != null
    && dismissedFocusOpenIntent !== focusOpenIntent;
  const isOpen = manuallyOpen || isIntentOpen;
  const isMediaIcon = iconVariant === 'media';

  useLayoutEffect(() => {
    if (
      disabled
      || focusOpenIntent == null
      || handledFocusOpenIntentRef.current === focusOpenIntent
    ) {
      return;
    }
    handledFocusOpenIntentRef.current = focusOpenIntent;
    triggerRef.current?.focus();
  }, [disabled, focusOpenIntent]);

  // Ending a session restores whatever the caller was showing before the first hover.
  // Selection is the one exit that ends it silently: onChange already supersedes the preview.
  useLayoutEffect(() => {
    previewEndRef.current = onOptionPreviewEnd;
  }, [onOptionPreviewEnd]);

  const endPreviewSession = useCallback(({ notify }) => {
    if (!previewSessionRef.current) return;
    previewSessionRef.current = false;
    if (notify && typeof previewEndRef.current === 'function') {
      previewEndRef.current();
    }
  }, []);

  // Restore an abandoned preview during layout teardown. Parent preview hooks tear down
  // later in the passive phase, so the restore remains a discard instead of becoming a
  // post-teardown draft that is automatically committed to history.
  useLayoutEffect(
    () => () => endPreviewSession({ notify: true }),
    [endPreviewSession],
  );

  // Stable identity is load-bearing, not tidiness. DropdownMenu keys its outside-click and
  // Escape listeners on this callback, and reinstalls the outside-click one behind a
  // setTimeout(0). Auditioning republishes on every hover, so an unstable closeMenu would
  // reopen that gap continuously and swallow clicks landing inside it.
  const closeMenu = useCallback(() => {
    endPreviewSession({ notify: true });
    setManuallyOpen(false);
    if (focusOpenIntent != null) setDismissedFocusOpenIntent(focusOpenIntent);
  }, [endPreviewSession, focusOpenIntent]);

  const previewOption = (option) => {
    if (!previewEnabled || option.disabled === true) return;
    previewSessionRef.current = true;
    onOptionPreview(option.value);
  };

  // Pointer left the surface but the menu is still open: show the base value again while
  // keeping the session (and the caller's snapshot) alive, so a fast leave/re-enter skim
  // cannot capture a previewed value as the new base.
  const handleSurfacePointerLeave = () => {
    if (!previewEnabled || !previewSessionRef.current) return;
    onOptionPreview(null);
  };

  const handleSurfaceFocusLeave = (event) => {
    if (
      !previewEnabled
      || !previewSessionRef.current
      || event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    onOptionPreview(null);
  };

  // Get current label from options
  const currentOption = options.find(opt => opt.value === value);
  
  // Render the text portion of the label (without icon)
  const renderLabelText = (option) => {
    if (option.modelName) {
      return (
        <>
          {option.label}
          <span className="dropdown-secondary-text">
            {option.modelName}
          </span>
        </>
      );
    }
    return option.label;
  };

  const renderTriggerLabelText = (option) => {
    if (!showSecondaryInTrigger || !option.modelName) {
      return option.label;
    }
    return renderLabelText(option);
  };

  // Render icon/indicator for the right side of a menu item
  const renderIcon = (option) => {
    if (!option?.iconKey) return null;
    // Special sentinel: render a checkmark instead of a model icon
    if (option.iconKey === '__check__') {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }
    return <ModelIcon iconKey={option.iconKey} size="xs" />;
  };
  
  const displayLabel = multiSelect ? placeholder : (currentOption ? renderTriggerLabelText(currentOption) : placeholder);
  const displayLeadingIcon = multiSelect ? null : currentOption?.icon;
  const shouldRenderLeadingIcon = Boolean(displayLeadingIcon)
    || (isMediaIcon && !multiSelect);
  const displayIcon = multiSelect ? null : (currentOption ? renderIcon(currentOption) : null);
  const accessibleLabel = ariaLabel
    ? `${ariaLabel}: ${currentOption?.label || placeholder}`
    : undefined;
  const triggerClasses = [
    'select-trigger',
    appearance === 'silent' && 'select-trigger--silent',
    isMediaIcon && 'select-trigger--media-icons',
  ].filter(Boolean).join(' ');

  const handleSelect = (optionValue, optionDisabled = false) => {
    if (optionDisabled) return;
    endPreviewSession({ notify: false });
    onChange(optionValue);
    if (!multiSelect) closeMenu();
  };

  return (
    <div>
      {/* Label */}
      {label && (
        typeof label === 'string' ? (
          <label className="param-label">{label}</label>
        ) : (
          <div className="param-label-flex">{label}</div>
        )
      )}

      {/* Dropdown Container */}
      <div className="select-field">
        {/* Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => {
            if (disabled) return;
            if (isOpen) closeMenu();
            else setManuallyOpen(true);
          }}
          disabled={disabled}
          className={triggerClasses}
          data-open={isOpen}
          aria-expanded={isOpen}
          aria-label={accessibleLabel}
        >
          {shouldRenderLeadingIcon && (
            <span
              className={[
                'select-trigger__icon',
                isMediaIcon && 'ui-media-thumbnail-backdrop',
              ].filter(Boolean).join(' ')}
              data-icon-variant={isMediaIcon ? 'media' : undefined}
              data-position="leading"
            >
              {displayLeadingIcon}
            </span>
          )}

          <span className="select-trigger__label">{displayLabel}</span>

          {/* Model icon (if provided) */}
          {displayIcon && (
            <span className="select-trigger__icon">
              {displayIcon}
            </span>
          )}

          {/* Dropdown arrow */}
          <span className="select-trigger__chevron" aria-hidden="true">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>

        <DropdownMenu
          open={isOpen}
          onClose={closeMenu}
          align="left"
          position={position}
          size="compact"
          matchTriggerWidth
          portalRoot={portalRoot}
          outsideCloseEvent={isIntentOpen ? 'pointerdown' : 'click'}
          onSurfacePointerLeave={previewEnabled ? handleSurfacePointerLeave : null}
          onSurfaceFocusLeave={previewEnabled ? handleSurfaceFocusLeave : null}
        >
          {options.map((option, index) => {
            const isActive = multiSelect
              ? option.iconKey === '__check__'
              : value === option.value;
            // A heading opens each run of options sharing a group. It is a plain div: not
            // focusable, not clickable, and not a popover dismiss target, so it can never
            // be selected or close the menu.
            const showGroupLabel = Boolean(option.group)
              && option.group !== (index > 0 ? options[index - 1]?.group : undefined);
            const menuItem = (
              <MenuItem
                key={option.value}
                onClick={() => handleSelect(option.value, option.disabled)}
                onMouseEnter={previewEnabled ? () => previewOption(option) : undefined}
                onFocus={previewEnabled ? () => previewOption(option) : undefined}
                active={isActive}
                disabled={option.disabled === true}
                dismissPopover={option.disabled !== true}
                size="compact"
                icon={option.icon}
                iconVariant={iconVariant}
                secondaryText={renderIcon(option)}
                selectionVariant={selectionVariant}
              >
                {renderLabelText(option)}
              </MenuItem>
            );
            if (!showGroupLabel) return menuItem;
            return (
              <Fragment key={`${option.group}-${option.value}`}>
                <div className="select-group-label">{option.group}</div>
                {menuItem}
              </Fragment>
            );
          })}
          {renderMenuFooter ? renderMenuFooter() : null}
        </DropdownMenu>
      </div>
    </div>
  );
};

export default memo(Select);
